import { call, put, SagaGenerator, select, takeLatest, takeMaybe } from "typed-redux-saga";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";

import { createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { executeBusy } from "../../../utilities/workflowUtils";
import { assertTrue } from "../../../utilities/assert";

import { ArchiveModuleFormDescriptor } from "../descriptors";
import { isArchiveModalDocumentData, ArchiveModalDocumentData } from "../archiveModalTypes";
import { cancelArchival, EldoradoCancelArchivePayload } from "../actions";

import { cancelEldoradoArchiveTask } from "./cancelEldoradoArchiveTask";

export function* cancelEldoradoArchiveSaga(): SagaGenerator<void> {
	yield* takeLatest(createEventButtonActionMatcher("event_cancel"), function* (action) {
		const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
		if (!ArchiveModuleFormDescriptor.isInstance(activity?.descriptor)) {
			return;
		}
		yield* call(executeBusy, function* () {
			const data = yield* select(ActivitySelectors.data(action.payload.activityId));
			assertTrue(isArchiveModalDocumentData(data));
			const root = data.document.root;
			const archiveSystem = activity.descriptor.archiveSystem === "xakte" ? "xakte" : "eldorado";
			if (root.metadata?.jobUuid) {
				const cancelPayload: EldoradoCancelArchivePayload = {
					archiveSystem,
					documentReference: activity.descriptor.instance,
					activityId: action.payload.activityId,
					implicit: false,
					closeActivity: true,
					reopenWorkflowsActivities: !!root.metadata.archivePreviewUrl
				};
				yield* call(cancelEldoradoArchiveTask, cancelPayload);
			} else {
				const isDirty = yield* select(ActivitySelectors.dirty(action.payload.activityId));
				const cancelPayload: EldoradoCancelArchivePayload = {
					archiveSystem,
					activityId: action.payload.activityId,
					implicit: !isDirty,
					closeActivity: true
				};
				yield* call(cancelEldoradoArchiveTask, cancelPayload);
			}
		});
	});
}

export function* cancelEldoradoArchiveRemoteSaga(): SagaGenerator<void> {
	yield* takeLatest(cancelArchival.match, function* (action) {
		yield* call(executeBusy, function* () {
			const activity = action.payload.activityId
				? yield* select(ActivitySelectors.activityById(action.payload.activityId))
				: undefined;
			const archiveSystem = ArchiveModuleFormDescriptor.isInstance(activity?.descriptor)
				? (activity.descriptor.archiveSystem ?? "eldorado")
				: action.payload.archiveSystem;
			yield* call(cancelEldoradoArchiveTask, { ...action.payload, archiveSystem });
		});
	});
}

export function* handleArchiveCancelRequestedSaga(): SagaGenerator<void> {
	yield* takeLatest(ActivityActions.cancelRequested, function* (action) {
		const { activityIds } = action.payload;

		let matchedActivityId: string | undefined;
		let savedData: ArchiveModalDocumentData | undefined;
		let documentReference: string | undefined;
		let archiveSystem: ArchiveModuleFormDescriptor["archiveSystem"] | undefined;
		for (const activityId of activityIds) {
			const activity = yield* select(ActivitySelectors.activityById(activityId));
			if (activity && ArchiveModuleFormDescriptor.isInstance(activity.descriptor)) {
				matchedActivityId = activityId;
				documentReference = activity.descriptor.instance;
				archiveSystem = activity.descriptor.archiveSystem ?? "eldorado";
				const data = yield* select(ActivitySelectors.data(activityId));
				if (isArchiveModalDocumentData(data)) {
					savedData = data;
				}
				break;
			}
		}

		if (
			!matchedActivityId ||
			!savedData ||
			!documentReference ||
			(!savedData.document.root.metadata?.archivePreviewUrl && !savedData.document.root.metadata?.jobUuid)
		) {
			return;
		}

		const result = yield* takeMaybe(ActivityActions.responseCancelRequested.match);
		if (result.payload.cancelled) {
			yield* put(
				cancelArchival({
					documentReference,
					archiveSystem,
					activityId: matchedActivityId,
					implicit: true,
					closeActivity: true,
					reopenWorkflowsActivities:
						archiveSystem === "eldorado" && !!savedData.document.root.metadata?.archivePreviewUrl
				})
			);
		}
	});
}
